import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { apiRequest, ApiError } from "@/api/client";
import { useAuthStore } from "@/auth/authStore";
import { hasGlobalRole } from "@/auth/permissions";
import type { AuthSessionContext, TokenPayload } from "@/auth/sessionTypes";
import { useLanguage } from "@/i18n/LanguageContext";

import {
  mustChangePasswordFromPayload,
  resolveConferencePostLoginPath,
} from "./postLoginRedirect";

interface LoginResponse {
  data: TokenPayload;
}

function buildSession(payload: TokenPayload): AuthSessionContext {
  return (
    payload.session ?? {
      user: payload.user,
      global_roles: [],
      member_profile: null,
      journal_memberships: [],
    }
  );
}

export function ConferenceLoginPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const globalRoles = useAuthStore((s) => s.globalRoles);
  const user = useAuthStore((s) => s.user);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setSessionContext = useAuthStore((s) => s.setSessionContext);
  const markHydrated = useAuthStore((s) => s.markHydrated);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (accessToken) {
    if (user?.must_change_password) {
      return <Navigate to="/set-password" replace />;
    }
    return <Navigate to={hasGlobalRole(globalRoles, "admin") ? "/admin" : "/dashboard"} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const json = await apiRequest<LoginResponse>("/v1/auth/login", {
        method: "POST",
        body: { email, password },
        anonymous: true,
      });
      setTokens({
        accessToken: json.data.access_token,
        refreshToken: json.data.refresh_token,
      });
      const session = buildSession(json.data);
      setSessionContext(session);
      markHydrated();
      const dest = mustChangePasswordFromPayload(json.data)
        ? "/set-password"
        : await resolveConferencePostLoginPath(session, json.data);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 420 }}>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.login.title}
      </h1>
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      <form className="dn-form" onSubmit={onSubmit}>
        <label>
          {t.register.email}
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
        </label>
        <label>
          {t.register.password}
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required
          />
        </label>
        <button className="dn-btn dn-btn-primary" disabled={submitting} type="submit">
          {t.login.submit}
        </button>
      </form>
      <p style={{ marginTop: "1rem" }}>
        {t.login.noAccount} <Link to="/register">{t.nav.register}</Link>
      </p>
    </div>
  );
}
